import { MetaData } from '../MetaData';
import { Location } from './../location';
import { Mot } from '../mots/mot';
import { Line } from '../line';
import { TimetableTrafficNode } from '../trafficNodes/timetableTrafficNode';
import * as dayjs from 'dayjs';
import { Disruption } from '../disruptions/disruption';
import { Transition } from '../transitions/transition';
import { isNullOrUndefined, isUndefined } from 'util';
import { TurnByTurnInstruction } from '../turnByTurn/turnByTurnInstruction';
/**
 * class {PartialConnection}
 * should be used to display partialConnection related information
 */
export class PartialConnection extends MetaData {
    /**
     * constructor of class
     * please use factory instead
     * @see {this.factory)
     * @param id
     * @param mot Mot object with information about the method of transport
     * @param line Line object with information about the traffic line used
     * @param nodes The nodes of this partial connection from origin to destination
     * @param disruptions List of Disruption objects to hold information on
     * line disruptions and changes
     * @param transitions List of Transition objects, to describe the footpath
     * from one mot to the next
     * @param additionalInfo List of additional Infos, given by API
     * @param serviceHotLine Number for AST
     * @param pathOnMap Array of Locations that describes the path the partial
     * @param metaData Optional Data
     * @param actualStartDateTime The realTime StartTime
     * @param actualEndDateTime The realTime EndTime
     * connection takes on the map
     */
    constructor(id, mot, line, nodes, disruptions, transitions, additionalInfo, serviceHotLine, pathOnMap, realTimeControlled, turnByTurn, metaData) {
        super(metaData);
        this._id = id;
        this._mot = mot;
        this._line = line;
        this._nodes = nodes;
        this._disruptions = disruptions;
        this._transitions = transitions;
        this._additionalInfo = additionalInfo;
        this._serviceHotLine = serviceHotLine;
        this._pathOnMap = pathOnMap;
        this._realTimeControlled = realTimeControlled;
        this._turnByTurn = turnByTurn;
    }
    /**
     * Factory method to create an instance of this class without
     * having to correctly fill the constructor.
     * !!! You should always use this instead of the constructor !!!
     * @param values
     * @returns {PartialConnection}
     */
    static factory(values) {
        const sanitizedValues = {};
        sanitizedValues.id = values.id || '';
        sanitizedValues.mot = values.mot ? Mot.factory(values.mot) : Mot.factory({});
        sanitizedValues.line = values.line ? Line.factory(values.line) : Line.factory({});
        sanitizedValues.nodes = [];
        if (values.nodes) {
            for (const node of values.nodes) {
                sanitizedValues.nodes.push(TimetableTrafficNode.factory(node));
            }
        }
        sanitizedValues.disruptions = [];
        if (values.disruptions) {
            for (const disruption of values.disruptions) {
                sanitizedValues.disruptions.push(Disruption.factory(disruption));
            }
        }
        sanitizedValues.transitions = [];
        if (values.transitions) {
            for (const transition of values.transitions) {
                sanitizedValues.transitions.push(Transition.factory(transition));
            }
        }
        sanitizedValues.additionalInfo = values.additionalInfo ? values.additionalInfo : [];
        sanitizedValues.serviceHotLine = values.serviceHotLine ? values.serviceHotLine : '';
        sanitizedValues.pathOnMap = [];
        if (values.pathOnMap) {
            for (const location of values.pathOnMap) {
                sanitizedValues.pathOnMap.push(Location.factory(location));
            }
        }
        sanitizedValues.realTimeControlled = !isUndefined(values.realTimeControlled) ? values.realTimeControlled : false;
        sanitizedValues.turnByTurn = [];
        if (values.turnByTurn) {
            for (const turnByTurn of values.turnByTurn) {
                sanitizedValues.turnByTurn.push(TurnByTurnInstruction.factory(turnByTurn));
            }
        }
        sanitizedValues.metaData = !isUndefined(values.metaData) ? values.metaData : {};
        return new PartialConnection(sanitizedValues.id, sanitizedValues.mot, sanitizedValues.line, sanitizedValues.nodes, sanitizedValues.disruptions, sanitizedValues.transitions, sanitizedValues.additionalInfo, sanitizedValues.serviceHotLine, sanitizedValues.pathOnMap, sanitizedValues.realTimeControlled, sanitizedValues.turnByTurn, sanitizedValues.metaData);
    }
    /**
     * gets the internal _id
     * @returns {string}
     */
    get id() {
        return this._id;
    }
    /**
     * sets the internal _id
     * @param value
     */
    set id(value) {
        this._id = value;
    }
    /**
     * gets all timetableTrafficNodes for current partialConnection
     * @returns {TimetableTrafficNode[]}
     */
    get nodes() {
        return this._nodes;
    }
    /**
     * sets all timetableTrafficNodes for current partialConnection
     * @param value
     */
    set nodes(value) {
        this._nodes = value;
    }
    /**
     * gets the line of this partialConnection
     * @returns {Line}
     */
    get line() {
        return this._line;
    }
    /**
     * sets the line of this partialConnection
     * @param value
     */
    set line(value) {
        this._line = value;
    }
    /**
     * gets the mot information for this partialConnection
     * @returns {Mot}
     */
    get mot() {
        return this._mot;
    }
    /**
     * sets the mot information for this partialConnection
     * @param value
     */
    set mot(value) {
        this._mot = value;
    }
    /**
     * gets the disruption information for this partialConnection
     * @returns {Disruption[]}
     */
    get disruptions() {
        return this._disruptions;
    }
    /**
     * sets the disruption information for this partialConnection
     * @param value
     */
    set disruptions(value) {
        this._disruptions = value;
    }
    /**
     * gets the transition info array for this partialConnection
     * @returns {Transition[]}
     */
    get transitions() {
        return this._transitions;
    }
    /**
     * sets the transition info array for this partialConnection
     * @param value
     */
    set transitions(value) {
        this._transitions = value;
    }
    /**
     * gets the array of additional info strings from the api
     * @returns {string[]}
     */
    get additionalInfo() {
        return this._additionalInfo;
    }
    /**
     * sets the array of additional info strings from the api
     * @param value
     */
    set additionalInfo(value) {
        this._additionalInfo = value;
    }
    /**
     * gets the serviceHotLine
     * @returns {string}
     */
    get serviceHotLine() {
        return this._serviceHotLine;
    }
    /**
     * sets the serviceHotLine
     * @param value
     */
    set serviceHotLine(value) {
        this._serviceHotLine = value;
    }
    /**
     * gets the array of locations that describes the taken path
     * @returns {Location[]} Location Array
     */
    get pathOnMap() {
        return this._pathOnMap;
    }
    /**
     * sets the array of locations that describes the taken path
     * @param value Location Array
     */
    set pathOnMap(value) {
        this._pathOnMap = value;
    }
    /**
     * gets the boolean of whether the connection is real time controlled
     * @returns {boolean} true if realtime delays are available
     */
    get realTimeControlled() {
        return this._realTimeControlled;
    }
    /**
     * sets the boolean of whether the connection is real time controlled
     * @param value boolean
     */
    set realTimeControlled(value) {
        this._realTimeControlled = value;
    }
    /**
     * gets the list of instructions for the turn by turn navigation
     * @returns {TurnByTurnInstruction[]} Array of individual turn by turn operations
     */
    get turnByTurn() {
        return this._turnByTurn;
    }
    /**
     * sets the list of instructions for the turn by turn navigation
     * @param value
     */
    set turnByTurn(value) {
        this._turnByTurn = value;
    }
    // --- implicit getters ---
    /**
     * get the origin of the current partialConnection based on existing trafficNodes
     * (gets the first trafficNode)
     * @returns {TimetableTrafficNode} or {undefined} if no trafficNode is in {_nodes}
     */
    get origin() {
        return this._nodes[0] ? this._nodes[0] : undefined;
    }
    /**
     * get the destination of the current partialConnection based on existing trafficNodes
     * (gets the last trafficNode)
     * @returns {TimetableTrafficNode} or {undefined} if no trafficNode is in {_nodes}
     */
    get destination() {
        return this._nodes.length > 0 && this._nodes[this._nodes.length - 1]
            ? this._nodes[this._nodes.length - 1]
            : undefined;
    }
    /**
     * get the start date-time of the current partialConnection based on existing trafficNodes
     * (gets start date-time of first trafficNode)
     * @returns {Date} or {undefined} if no trafficNode is in {_nodes}
     */
    get startDateTime() {
        return this._nodes[0] ? this._nodes[0].departureDateTime : undefined;
    }
    /**
     * get the end date-time of the current partialConnection based on existing trafficNodes
     * (gets end date-time of last trafficNode)
     * @returns {Date} or {undefined} if no trafficNode is in {_nodes}
     */
    get endDateTime() {
        return this._nodes.length > 0 && this._nodes[this._nodes.length - 1] ? this._nodes[this._nodes.length - 1].arrivalDateTime : undefined;
    }
    /**
     * gets the actual Start Date
     * @returns {Date} Date
     */
    get actualStartDateTime() {
        return this._nodes[0] ? this._nodes[0].actualDepartureDateTime : undefined;
    }
    /**
     * gets the actual End Date
     * @returns {Date} Date
     */
    get actualEndDateTime() {
        return this._nodes.length > 0 && this._nodes[this._nodes.length - 1]
            ? this._nodes[this._nodes.length - 1].actualArrivalDateTime
            : undefined;
    }
    /**
     * gets the duration of the current partialConnection based on existing trafficNodes
     * (gets the calculated duration from first trafficNode start date-time to last trafficNode end date-time)
     * @returns {number}
     */
    get duration() {
        if (!isNullOrUndefined(this.actualStartDateTime) && !isNullOrUndefined(this.actualEndDateTime)) {
            return dayjs(this.actualEndDateTime).diff(dayjs(this.actualStartDateTime), 'minute');
        }
        else if (!isNullOrUndefined(this.actualStartDateTime) && !isNullOrUndefined(this.endDateTime)) {
            return dayjs(this.endDateTime).diff(dayjs(this.actualStartDateTime), 'minute');
        }
        else if (!isNullOrUndefined(this.startDateTime) && !isNullOrUndefined(this.actualEndDateTime)) {
            return dayjs(this.actualEndDateTime).diff(dayjs(this.startDateTime), 'minute');
        }
        else {
            return dayjs(this.endDateTime).diff(dayjs(this.startDateTime), 'minute');
        }
    }
    /**
     * gets the departure delay
     * @returns {number[]}
     */
    get delayDeparture() {
        const actualStart = this.actualStartDateTime ? this.actualStartDateTime : this.startDateTime;
        return dayjs(actualStart).diff(dayjs(this.origin.departureDateTime), 'minute');
    }
    /**
     * gets the arrival delay
     * @returns {number}
     */
    get delayArrival() {
        const actualEnd = this.actualEndDateTime ? this.actualEndDateTime : this.endDateTime;
        return dayjs(actualEnd).diff(dayjs(this.destination.arrivalDateTime), 'minute');
    }
}
//# sourceMappingURL=partialConnection.js.map